import React, { useMemo } from 'react'
import { Card } from 'react-bootstrap'
import { ChevronRight, FileText } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  DeviceAlertStatus,
  ISubscriptionDeviceRow,
  deriveDeviceStatus,
} from '@data/subscriptions/model/subscriptions.model'

interface SummaryCard {
  key: 'total' | DeviceAlertStatus
  label: string
  className: string
}

const SUMMARY_CARDS: SummaryCard[] = [
  { key: 'total', label: 'Total', className: 'sf-billing-summary__card--total' },
  { key: 'healthy', label: 'Healthy', className: 'sf-billing-summary__card--healthy' },
  { key: 'paid', label: 'Paid', className: 'sf-billing-summary__card--paid' },
  { key: 'upcoming', label: 'Upcoming', className: 'sf-billing-summary__card--upcoming' },
  { key: 'overdue', label: 'Overdue', className: 'sf-billing-summary__card--overdue' },
  { key: 'restricted', label: 'Restricted', className: 'sf-billing-summary__card--restricted' },
  { key: 'pending', label: 'Pending 1st Payment', className: 'sf-billing-summary__card--pending' },
]

/** Minor units -> a display string. Falls back to EUR when unknown. */
function formatAmount(minor: number, currency: string | null): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(minor / 100)
}

interface Props {
  devices: ISubscriptionDeviceRow[] | undefined
}

const BillingOverview: React.FC<Props> = ({ devices }) => {
  const navigate = useNavigate()

  const counts = useMemo(() => {
    const acc: Record<'total' | DeviceAlertStatus, number> = {
      total: devices?.length ?? 0,
      healthy: 0,
      paid: 0,
      upcoming: 0,
      overdue: 0,
      restricted: 0,
      pending: 0,
      no_subscription: 0,
    }
    for (const device of devices ?? []) {
      acc[deriveDeviceStatus(device)] += 1
    }
    return acc
  }, [devices])

  // Monetary totals per status. A device whose next term is already paid is
  // counted in `paid` and must not also appear in `upcoming` — that
  // double-count is what made the dashboard's figures wrong (SF-APP-007 req. 6).
  const totals = useMemo(() => {
    const acc: Partial<Record<'total' | DeviceAlertStatus, number>> = {}
    let currency: string | null = null
    for (const device of devices ?? []) {
      const price = device.monitoringPriceMinor ?? 0
      if (!price) continue
      currency = currency ?? device.currency
      const status = deriveDeviceStatus(device)
      acc.total = (acc.total ?? 0) + price
      acc[status] = (acc[status] ?? 0) + price
    }
    return { amounts: acc, currency }
  }, [devices])

  if (!devices || devices.length === 0) return null

  return (
    <Card className="sf-billing-overview mb-3">
      <Card.Body>
        <h5 className="sf-billing-overview__title mb-3">Billing Overview</h5>
        <div className="text-muted small fw-semibold mb-2">DEVICE STATUS SUMMARY</div>
        <div className="sf-billing-summary">
          {SUMMARY_CARDS.map((card) => (
            <div key={card.key} className={`sf-billing-summary__card ${card.className}`}>
              <div className="sf-billing-summary__count">{counts[card.key]}</div>
              <div className="sf-billing-summary__label">{card.label}</div>
              {!!totals.amounts[card.key] && (
                <div className="sf-billing-summary__amount">
                  {formatAmount(totals.amounts[card.key] as number, totals.currency)}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            type="button"
            className="btn btn-link btn-sm p-0 d-inline-flex align-items-center"
            onClick={() => navigate('/subscriptions')}
          >
            <FileText size={14} className="me-1" />
            View invoice history
          </button>
          <button
            type="button"
            className="btn btn-link btn-sm p-0 d-inline-flex align-items-center"
            onClick={() => navigate('/subscriptions')}
          >
            Go to Detailed View
            <ChevronRight size={14} className="ms-1" />
          </button>
        </div>
      </Card.Body>
    </Card>
  )
}

export default BillingOverview
